import { z } from 'zod';

export const PROFILE_SEARCH_BY = ['auto', 'external_id', 'alias', 'email', 'phone', 'push_token', 'profile_id'] as const;
export type ProfileSearchBy = (typeof PROFILE_SEARCH_BY)[number];

export const ProfileSearchQuery = z.object({
  q: z.string().trim().min(1).max(320),
  by: z.enum(PROFILE_SEARCH_BY).default('auto'),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ProfileSearchQuery = z.infer<typeof ProfileSearchQuery>;

export const ProfileStatus = z.enum(['active', 'merged', 'erased']);

/** One search hit; email/phone are masked unless the caller holds `pii.view`. */
export const ProfileSearchHit = z.object({
  profileId: z.uuid(),
  externalId: z.string().nullable(),
  matchedBy: z.enum(PROFILE_SEARCH_BY).exclude(['auto']),
  status: ProfileStatus,
  mergedIntoProfileId: z.uuid().nullable(),
  createdFrom: z.enum(['sdk', 'server', 'import', 'admin']),
  lastSeenAt: z.iso.datetime({ offset: true }).nullable(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  emailMasked: z.string().nullable(),
  phoneMasked: z.string().nullable(),
});
export type ProfileSearchHit = z.infer<typeof ProfileSearchHit>;

const Timestamp = z.iso.datetime({ offset: true });

/** A device (installation) of a profile (BB-31). The raw push token never leaves the service: only its mask and hash. */
export const DeviceView = z.object({
  installationId: z.uuid(),
  appInstanceId: z.uuid(),
  platform: z.enum(['android', 'ios', 'web']),
  deviceModel: z.string().nullable(),
  osVersion: z.string().nullable(),
  appVersion: z.string().nullable(),
  sdkVersion: z.string().nullable(),
  locale: z.string().nullable(),
  timezone: z.string().nullable(),
  pushPermission: z.enum(['granted', 'denied', 'provisional', 'not_determined', 'unknown']),
  foregroundPushEnabled: z.boolean(),
  status: z.enum(['active', 'uninstalled_suspected']),
  uninstalledAt: Timestamp.nullable(),
  firstSeenAt: Timestamp,
  lastSeenAt: Timestamp,
  token: z
    .object({
      provider: z.enum(['fcm', 'apns']),
      status: z.enum(['active', 'replaced', 'unregistered', 'invalid', 'stale']),
      tokenMasked: z.string(),
      tokenHash: z.string(),
      lastRefreshedAt: Timestamp,
      invalidReason: z.string().nullable(),
    })
    .nullable(),
});
export type DeviceView = z.infer<typeof DeviceView>;
export const DeviceList = z.object({ items: z.array(DeviceView) });
export type DeviceList = z.infer<typeof DeviceList>;

export const IdentityVerificationMode = z.enum(['off', 'optional', 'required']);

export const UpdateIdentityVerificationBody = z
  .strictObject({
    mode: IdentityVerificationMode,
    /** HTTPS, or plain HTTP to localhost for development. */
    jwksUrl: z
      .url({ protocol: /^https?$/ })
      .refine((url) => url.startsWith('https://') || /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//.test(url), 'jwksUrl must use https')
      .nullable()
      .optional(),
    issuer: z.string().min(1).max(255).nullable().optional(),
    audience: z.string().min(1).max(255).nullable().optional(),
    allowedAlgs: z.array(z.enum(['RS256', 'ES256'])).min(1).optional(),
  })
  .refine((body) => body.mode === 'off' || Boolean(body.jwksUrl && body.issuer && body.audience), {
    message: 'jwksUrl, issuer and audience are required unless mode is off',
    path: ['jwksUrl'],
  });
export type UpdateIdentityVerificationBody = z.infer<typeof UpdateIdentityVerificationBody>;

/**
 * Merge of two identified profiles by hand (BB-159, DAT-002): the source's data moves to the target, the target's
 * attributes win (empty ones are filled from the source), subscriptions keep the stricter state, the source's journeys
 * end, past message facts and reports stay as they were, and the source's external ID keeps resolving to the target.
 */
const ProfileRef = z.union([z.strictObject({ profileId: z.uuid() }), z.strictObject({ externalId: z.string().min(1).max(128) })]);
export const MergeProfilesBody = z.strictObject({
  source: ProfileRef,
  target: ProfileRef,
  reason: z.string().trim().min(1).max(500),
  /** Only report what would move. */
  dryRun: z.boolean().default(false),
});
export type MergeProfilesBody = z.infer<typeof MergeProfilesBody>;
/** `POST /server/v1/profiles/merge` (scope `profiles.write`). */
export const ServerMergeProfilesBody = z.strictObject({ sourceExternalId: z.string().min(1).max(128), targetExternalId: z.string().min(1).max(128), reason: z.string().trim().min(1).max(500).optional() });

export interface MergePreview {
  source: { profileId: string; externalId: string | null };
  target: { profileId: string; externalId: string | null };
  /** Rows of the source per table that will move to the target. */
  moves: Record<string, number>;
  /** Attributes both profiles have with different values: the target's value stays. */
  attributeConflicts: string[];
  /** Attributes only the source has: they fill the target. */
  attributesFilled: string[];
  /** Subscriptions whose state changes on the target (the stricter one wins). */
  subscriptionChanges: Array<{ kind: 'channel' | 'group'; name: string; from: string | null; to: string }>;
  /** Journeys of the source that will end with reason `merged`. */
  journeysEnded: number;
}

export interface MergeView extends MergePreview {
  mergeId: string;
  status: 'pending' | 'completed' | 'failed';
}

/** An AppsFlyer install of a profile (BB-131); `appsflyerId` is masked without `pii.view`. */
export interface InstallAttributionView {
  appsflyerId: string;
  source: string | null;
  sourceKind: 'network' | 'affiliate' | 'owned' | null;
  campaign: string | null;
  adgroup: string | null;
  ad: string | null;
  installAt: string | null;
  attributedAt: string | null;
  receivedVia: 'postback' | 'sdk';
  linkedVia: 'sdk' | 'customer_user_id';
}
